/* MSAL auth helpers for Project Everest (Entra ID SSO).

   Uses @azure/msal-browser directly (no React provider) so it slots into the existing
   imperative getBackendAuthToken() call site. The flow:
     1. initAuth()  — called once before first render (index.js); processes any sign-in
        redirect, captures any returned Entra error, and sets the active account.
     2. signIn()    — full-page redirect to BJ Entra ID when no account is present.
     3. acquireBackendToken() — returns the ID token to forward to the governed backend. */
import {
  PublicClientApplication,
  InteractionRequiredAuthError,
  EventType,
} from "@azure/msal-browser";

import { msalConfig, loginRequest } from "./authConfig";

export const msalInstance = new PublicClientApplication(msalConfig);

let _ready = null;
let _authError = null;

/* Initialize MSAL and process any redirect response. Idempotent — safe to await more than
   once. MUST resolve before the app reads the signed-in account, so index.js awaits it
   before the first render. NEVER rejects: a sign-in that returns an Entra error (declined
   consent, conditional-access block, interrupted MFA) is captured in _authError so the UI
   can surface it instead of blindly redirecting back to Entra in a loop. */
export function initAuth() {
  if (_ready) return _ready;
  _ready = (async () => {
    try {
      await msalInstance.initialize();
      const result = await msalInstance.handleRedirectPromise();
      if (result?.account) {
        msalInstance.setActiveAccount(result.account);
      } else if (!msalInstance.getActiveAccount()) {
        const accounts = msalInstance.getAllAccounts();
        if (accounts.length) msalInstance.setActiveAccount(accounts[0]);
      }
    } catch (err) {
      _authError = err;
    }
  })();
  return _ready;
}

/* The last sign-in error returned from Entra (or null). Lets the gate show a message +
   Retry instead of auto-redirecting forever on a non-recoverable failure. */
export function getAuthError() {
  return _authError;
}

export function getActiveAccount() {
  return msalInstance.getActiveAccount();
}

/* Subscribe to MSAL account changes (sign-in / token / sign-out / redirect-end) so React
   state can track the live signed-in account instead of a value frozen at first render.
   Returns an unsubscribe function. */
export function subscribeAccount(cb) {
  const id = msalInstance.addEventCallback((event) => {
    switch (event?.eventType) {
      case EventType.LOGIN_SUCCESS:
      case EventType.ACQUIRE_TOKEN_SUCCESS:
        if (event.payload?.account) msalInstance.setActiveAccount(event.payload.account);
        cb(msalInstance.getActiveAccount());
        break;
      case EventType.HANDLE_REDIRECT_END:
        cb(msalInstance.getActiveAccount());
        break;
      case EventType.LOGOUT_SUCCESS:
        cb(null);
        break;
      default:
        break;
    }
  });
  return () => { if (id) msalInstance.removeEventCallback(id); };
}

/* Start an interactive sign-in (full-page redirect to BJ Entra ID). Clears any prior error
   so a user-initiated Retry isn't immediately suppressed by the gate. */
export function signIn() {
  _authError = null;
  return msalInstance.loginRedirect(loginRequest);
}

/* Sign out (full-page redirect, then back to the app origin). */
export function signOut() {
  const account = msalInstance.getActiveAccount();
  return msalInstance.logoutRedirect({ account });
}

/* Acquire a JWT to forward to the governed backend, or null if not signed in.

   Returns the ID token: its `aud` is this app's clientId and it carries the app-role claim
   — exactly what the backend (entra.py) validates. Silently renews when possible; on a
   session that needs interactive re-auth, bounces through a redirect (page navigates away).
   Other (transient/network) failures are logged — not silently swallowed — so a real
   acquisition failure isn't misreported to a signed-in user as "not signed in". */
export async function acquireBackendToken() {
  const account = msalInstance.getActiveAccount();
  if (!account) return null;
  try {
    const res = await msalInstance.acquireTokenSilent({ ...loginRequest, account });
    return res.idToken || null;
  } catch (err) {
    if (err instanceof InteractionRequiredAuthError) {
      await msalInstance.acquireTokenRedirect({ ...loginRequest, account });
      return null;
    }
    // eslint-disable-next-line no-console
    console.error("acquireTokenSilent failed", err);
    return null;
  }
}
